import { Box, Card, CardActionArea, CardContent, CardActions, Typography, Button, Chip, Stack } from "@mui/material";

export interface VideoCardProps {
  title: string;
  description?: string;
  categoryName?: string;
  url: string;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  usedInTitles?: string[];
  onAssign?: () => void;
}

// Extrait l'ID YouTube depuis différentes formes d'URL (y compris Shorts)
const getYouTubeId = (url: string): string | null => {
  const match = url.match(/(?:[?&]v=|\.be\/|\/embed\/|\/shorts\/)([\w-]+)/);
  return match ? match[1] : null;
};

const isSelfHostedVideo = (url: string): boolean => {
  const lower = (url || "").toLowerCase();
  return (
    lower.startsWith("/uploads/videos/") ||
    /\.(mp4|webm|ogg)(?:[?#]|$)/i.test(lower)
  );
};

const VideoCard = ({ title, description, categoryName, url, onView, onEdit, onDelete, usedInTitles, onAssign }: VideoCardProps) => {
  const ytId = getYouTubeId(url);
  const thumb = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;
  const selfHosted = !ytId && isSelfHostedVideo(url);

  return (
    <Card sx={{ width: "100%", display: "flex", flexDirection: "column", borderRadius: 3, boxShadow: 3, overflow: "hidden", bgcolor: "background.paper" }}>
      <CardActionArea onClick={onView} aria-label="Voir la vidéo" sx={{ position: "relative" }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            paddingBottom: "56.25%", // 16:9
            overflow: "hidden",
            backgroundColor: thumb ? "#222" : "#eee",
          }}
        >
          {thumb ? (
            <img
              src={thumb}
              alt={title}
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.85)" }}
              loading="lazy"
            />
          ) : selfHosted ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #000 100%)",
                color: "#fff",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Vidéo hébergée
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">Aperçu indisponible</Typography>
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              bgcolor: "rgba(0,0,0,0.55)",
              color: "#fff",
              px: 2,
              py: 1,
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
              display: "flex",
              alignItems: "center",
              minHeight: 48,
            }}
          >
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, flex: 1 }}>{title}</Typography>
            {categoryName && (
              <Chip label={categoryName} size="small" sx={{ ml: 1, bgcolor: "primary.main", color: "#fff", fontWeight: 500 }} />
            )}
          </Box>
        </Box>
      </CardActionArea>
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        )}
        {usedInTitles && usedInTitles.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, mb: 0.5 }}>
            {usedInTitles.slice(0, 6).map((ex, i) => (
              <Chip key={i} label={ex} size="small" sx={{ bgcolor: "background.default.dark", color: "primary.dark", fontWeight: 500 }} />
            ))}
            {usedInTitles.length > 6 && (
              <Chip label={`+${usedInTitles.length - 6}`} size="small" sx={{ bgcolor: "grey.300", color: "text.primary" }} />
            )}
          </Stack>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
        {onView && (
          <Button size="small" onClick={onView} sx={{ fontWeight: 600 }}>Voir</Button>
        )}
        {onEdit && (
          <Button size="small" onClick={onEdit} sx={{ fontWeight: 600 }}>Modifier</Button>
        )}
        {onAssign && (
          <Button size="small" onClick={onAssign} sx={{ fontWeight: 600 }}>Associer</Button>
        )}
        {onDelete && (
          <Button size="small" color="error" onClick={onDelete} sx={{ fontWeight: 600 }}>Supprimer</Button>
        )}
      </CardActions>
    </Card>
  );
};

export default VideoCard;
